import type {
  EdgeCall,
  EdgeDiscovery,
  Match,
  MatchState,
  Persona,
  PersonaKey,
  PersonaStats,
  DebateResponse,
} from '@sharp8/types';
import { stubs } from './stubs.js';

export interface FalconClientOptions {
  /** Base URL of the Falcon engine API, e.g. https://engine.falcon.ai. */
  baseUrl: string;
  /** Shared engine token. Single secret between Sharp8 backend and Falcon. */
  token: string;
  /** When true, methods return stubbed data without hitting the network. */
  stubMode?: boolean;
  /** Optional fetch impl override (for Cloudflare Workers, tests). */
  fetch?: typeof globalThis.fetch;
}

export interface EdgesAPI {
  today(opts?: { league?: string; limit?: number }): Promise<EdgeDiscovery>;
  forMatch(matchId: string): Promise<EdgeCall[]>;
}

export interface MatchAPI {
  get(matchId: string): Promise<Match>;
  state(matchId: string): Promise<MatchState>;
  preview(matchId: string, lang?: 'en' | 'zh' | 'ms' | 'th'): Promise<{ body: string }>;
}

export interface PersonasAPI {
  list(): Promise<Persona[]>;
  stats(key: PersonaKey): Promise<PersonaStats>;
  portrait(key: PersonaKey): Promise<{ url: string }>;
}

export interface DebateAPI {
  ask(matchId: string, query: string, lang?: string): Promise<DebateResponse>;
}

export interface FalconClient {
  edges: EdgesAPI;
  match: MatchAPI;
  personas: PersonasAPI;
  debate: DebateAPI;
}

/**
 * Construct a Falcon engine client. In production this hits the engine's
 * /v1/* API namespace; with stubMode=true it returns canned responses
 * (useful for mini-app dev before Falcon's /v1/* lands).
 */
export function createFalconClient(opts: FalconClientOptions): FalconClient {
  const fetchImpl = opts.fetch ?? globalThis.fetch;
  const stub = opts.stubMode ?? false;

  // Tiny sleep used in stub mode so the UI gets a chance to render
  // loading states naturally. Disabled when STUB_FAST=1.
  const stubLatencyMs =
    typeof process !== 'undefined' && process.env?.STUB_FAST === '1' ? 0 : 120;
  const stubDelay = () =>
    stub && stubLatencyMs > 0
      ? new Promise((r) => setTimeout(r, stubLatencyMs))
      : Promise.resolve();

  // Internal fetch wrapper — all real-mode calls go through here.
  async function req<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await fetchImpl(`${opts.baseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${opts.token}`,
        ...(init?.headers ?? {}),
      },
    });
    if (!res.ok) {
      throw new Error(`Falcon engine ${path} failed: ${res.status} ${res.statusText}`);
    }
    return (await res.json()) as T;
  }

  return {
    edges: {
      today: async (o) => {
        if (stub) {
          await stubDelay();
          return stubs.edgesToday(o);
        }
        const p = new URLSearchParams();
        if (o?.league) p.set('league', o.league);
        if (o?.limit !== undefined) p.set('limit', String(o.limit));
        const qs = p.toString();
        return req<EdgeDiscovery>(`/v1/edges/today${qs ? '?' + qs : ''}`);
      },
      forMatch: async (matchId) => {
        if (stub) { await stubDelay(); return stubs.edgesForMatch(matchId); }
        return req<EdgeCall[]>(`/v1/edges/match/${matchId}`);
      },
    },
    match: {
      get: async (matchId) => {
        if (stub) { await stubDelay(); return stubs.matchGet(matchId); }
        return req<Match>(`/v1/match/${matchId}`);
      },
      state: async (matchId) => {
        if (stub) { await stubDelay(); return stubs.matchState(matchId); }
        return req<MatchState>(`/v1/match/${matchId}/state`);
      },
      preview: async (matchId, lang) => {
        if (stub) { await stubDelay(); return stubs.matchPreview(matchId, lang); }
        const qs = lang ? `?lang=${lang}` : '';
        return req<{ body: string }>(`/v1/match/${matchId}/preview${qs}`);
      },
    },
    personas: {
      list: async () => {
        if (stub) { await stubDelay(); return stubs.personasList(); }
        return req<Persona[]>('/v1/personas');
      },
      stats: async (key) => {
        if (stub) { await stubDelay(); return stubs.personasStats(key); }
        return req<PersonaStats>(`/v1/personas/${key}/stats`);
      },
      portrait: async (key) => {
        if (stub) { await stubDelay(); return stubs.personasPortrait(key); }
        return req<{ url: string }>(`/v1/personas/${key}/portrait`);
      },
    },
    debate: {
      ask: async (matchId, query, lang) => {
        if (stub) { await stubDelay(); return stubs.debateAsk(matchId, query, lang); }
        return req<DebateResponse>(`/v1/match/${matchId}/debate`, {
          method: 'POST',
          body: JSON.stringify({ query, lang }),
        });
      },
    },
  };
}

export type {
  EdgeCall,
  EdgeDiscovery,
  Match,
  MatchState,
  Persona,
  PersonaKey,
  PersonaStats,
  DebateResponse,
};
